import React, { useState, Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ANIMATION_CLASSES } from '../../utils/animations';
interface NavigationProps {
  isMobile?: boolean;
  closeMenu?: () => void;
}
export const Navigation = ({
  isMobile = false,
  closeMenu
}: NavigationProps) => {
  const [doMoreOpen, setDoMoreOpen] = useState(false);
  const location = useLocation();
  const navItems = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'About Us',
    path: '/about'
  }, {
    name: 'Trainings',
    path: '/training'
  }, {
    name: 'Gallery',
    path: '/gallery'
  }, {
    name: 'Join the Salon',
    path: '/joinnow'
  }, {
    name: 'Host a Salon',
    path: '/host-salon'
  }, {
    name: 'Partner with Us',
    path: '/partner-with-us'
  }, {
    name: 'Contact',
    path: '/contact'
  }];
  const doMoreItems = [{
    name: 'Salon Spotlight',
    path: '/salon-spotlight'
  }, {
    name: 'Join Our Team',
    path: '/join-team'
  }, {
    name: 'Stories',
    path: '/success-stories'
  }, {
    name: 'Blog',
    path: '/blog'
  }];
  const isActive = (path: string) => {
    if (path === '/' && location.pathname !== '/') return false;
    return location.pathname.startsWith(path);
  };
  // Mobile Navigation
  if (isMobile) {
    return <nav className="w-full" aria-label="Mobile Navigation" data-id="element-833" data-mp="38bhiefk38fei">
        <ul className="flex flex-col space-y-2" data-id="element-834" data-mp="389wisbix269s">
          {navItems.map((item, index) => <li key={item.name} className={ANIMATION_CLASSES.slideInFromLeft} style={{
          animationDelay: `${index * 50}ms`
        }} data-id="element-835" data-mp="33cxrs1ujufft" data-mpi={index}>
              <Link to={item.path} onClick={closeMenu} className={`block py-3 px-4 rounded-lg text-lg font-medium transition-colors ${isActive(item.path) ? 'bg-gray-50 text-primary border-l-4 border-secondary' : 'text-gray-600 hover:bg-gray-50 hover:text-primary'}`} aria-current={isActive(item.path) ? 'page' : undefined} data-id="element-836" data-mp="2uawvb7753xf7">
                {item.name}
              </Link>
            </li>)}

          {/* Mobile "Do More" Section */}
          <li className={`mt-4 pt-4 border-t border-gray-100 ${ANIMATION_CLASSES.slideInFromLeft}`} style={{
          animationDelay: `${navItems.length * 50}ms`
        }} data-id="element-837" data-mp="2yfin3nmizz18">
            <button onClick={() => setDoMoreOpen(!doMoreOpen)} className="w-full flex items-center justify-between py-3 px-4 rounded-lg text-lg font-medium text-gray-800 hover:bg-gray-50 transition-colors" aria-expanded={doMoreOpen} data-id="element-838" data-mp="2oq7hiayulci3">
              <span data-id="element-839" data-mp="3m4ti0oh72z7g">Do More</span>
              <ChevronDown size={20} className={`transition-transform duration-300 ${doMoreOpen ? 'rotate-180' : ''}`} data-id="element-840" data-mp="3m64st8925782" />
            </button>

            {doMoreOpen && <ul className={`pl-4 mt-2 space-y-1 ${ANIMATION_CLASSES.fadeIn}`} data-id="element-841" data-mp="15qhxr2tn9nd1">
                {doMoreItems.map(item => <li key={item.name} data-id="element-842" data-mp="1jk92lxwfkj2f">
                    <Link to={item.path} onClick={closeMenu} className={`block py-2 px-4 rounded-md text-base transition-colors ${isActive(item.path) ? 'text-secondary font-medium' : 'text-gray-500 hover:text-primary'}`} data-id="element-843" data-mp="1fhvu51qau71y">
                      <div className="flex items-center" data-id="element-844" data-mp="10spxnliuiuxr">
                        <ChevronRight size={16} className="mr-2 text-secondary" data-id="element-845" data-mp="vt3jn9v71anw" />
                        {item.name}
                      </div>
                    </Link>
                  </li>)}
              </ul>}
          </li>
        </ul>
      </nav>;
  }
  // Desktop Navigation
  return <nav aria-label="Main Navigation" data-id="element-846" data-mp="37h0aiyyz8j6a">
      <ul className="flex space-x-1 lg:space-x-2 flex-wrap items-center" data-id="element-847" data-mp="37i1mr6rqbbpl">
        {navItems.map((item, index) => <Fragment key={item.name} data-id="element-848" data-mp="3cf1sthokwegp" data-mpi={index}>
            <li className="relative flex items-center" data-id="element-849" data-mp="3hffzavaj7gcm">
              <Link to={item.path} className={`relative px-2 lg:px-3 py-2 text-sm lg:text-base font-medium transition-colors group focus-visible:ring-2 focus-visible:ring-secondary rounded-sm outline-none ${isActive(item.path) ? 'text-primary' : 'text-gray-600 hover:text-primary'}`} aria-current={isActive(item.path) ? 'page' : undefined} data-id="element-850" data-mp="3hh0wkso8dde6">
                {item.name}
                <span className={`absolute bottom-0 left-0 h-0.5 bg-secondary transition-all duration-300 ${isActive(item.path) ? 'w-full' : 'w-0 group-hover:w-full'}`} data-id="element-851" data-mp="8zmmaqfesn0b"></span>
              </Link>
            </li>
            {index < navItems.length - 1 && <span className="h-4 border-r border-gray-300 mx-1" aria-hidden="true" data-id="element-852" data-mp="1xgxmbvztn5md"></span>}
          </Fragment>)}

        <span className="h-4 border-r border-gray-300 mx-1" aria-hidden="true" data-id="element-853" data-mp="1okojs8gjrqw0"></span>

        {/* Desktop "Do More" Dropdown */}
        <li className="relative group" onMouseEnter={() => setDoMoreOpen(true)} onMouseLeave={() => setDoMoreOpen(false)} onFocus={() => setDoMoreOpen(true)} onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setDoMoreOpen(false);
        }
      }} data-id="element-854" data-mp="2h67yj00cz8fq">
          <button className={`flex items-center gap-1 px-2 lg:px-3 py-2 text-sm lg:text-base font-medium transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-sm outline-none ${doMoreOpen ? 'text-primary' : 'text-gray-600 hover:text-primary'}`} aria-expanded={doMoreOpen} aria-haspopup="true" data-id="element-855" data-mp="2zxqzz2eqiefn">
            Do More
            <ChevronDown size={16} className={`transition-transform duration-300 ${doMoreOpen ? 'rotate-180' : ''}`} data-id="element-856" data-mp="xd6go0qune5c" />
          </button>

          {doMoreOpen && <div className={`absolute top-full right-0 mt-2 w-56 bg-white border border-gray-100 rounded-lg shadow-xl py-2 z-50 ${ANIMATION_CLASSES.fadeIn}`} data-id="element-857" data-mp="ogfgpd96ff5h">
              {doMoreItems.map(item => <Link key={item.name} to={item.path} className="block px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 hover:text-primary transition-colors border-l-2 border-transparent hover:border-secondary" data-id="element-858" data-mp="lcsn7i75fq2">
                  {item.name}
                </Link>)}
            </div>}
        </li>
      </ul>
    </nav>;
};